import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, RadioGroup, FormControlLabel, Radio, Typography } from "@mui/material";
import { Save } from "lucide-react";
import { useSelector } from "react-redux";
import moment from "moment";
import { CalendarDate, today, getLocalTimeZone } from "@internationalized/date";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import AppButton from "../../../common_components/AppButton";
import { toast } from "../../../common_components/Toast/ToastUtils";
import { Dropdown, TextInput } from "../../../common_components/FormInputs";
import SingleDatePicker from "../../../common_components/daterangepicker/SingleDatePicker";
import { ApiCall } from "../../../helper/axios";
import { selectAllClientList, selectDesignationWiseUsers } from "../../../redux/clientDataSlice";
import { AM_DESIGNATIONS, collectUsersByDesignations } from "../../../utils/designationFilters";
const MODULE_KEY = "publisher_reporting";
// Designation picked as the default Account Manager when a client has several AMs
const AFFILIATE_AM_DESIGNATION = "Affiliate AM";
// Same option sets as the Add Article modal (Outreach Performance)
const TAG_OPTIONS = [
    { value: "Organic", label: "Organic" },
    { value: "Flat Fee/CPC", label: "Flat Fee/CPC" },
    { value: "Coupon", label: "Coupon" },
    { value: "SEO", label: "SEO" },
];
const TYPE_OPTIONS = [
    { value: "paid", label: "Paid" },
    { value: "nonpaid", label: "Non-Paid" },
];
const getInitialForm = () => ({
    clientId: "",
    accountManagerId: "",
    publisher: "",
    publishedDate: null,
    type: "",
    commissionRate: "",
    contactName: "",
    contactEmail: "",
    category: "",
    publication: "",
    title: "",
    articleLink: "",
    payPerPlacement: "no",
    tag: "",
    emailNotification: "email_affiliate_team",
});
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Field -> error message. Required: client, publisher, published date (never in the future), type, contact name,
// contact email (valid), category, publication, title, article link, tag. Optional: account manager, commission rate.
const validate = (form) => {
    const errors = {};
    if (!form.clientId)
        errors.clientId = "Client is required";
    if (!form.publisher)
        errors.publisher = "Publisher / Partner is required";
    if (!form.publishedDate)
        errors.publishedDate = "Published date is required";
    else if (form.publishedDate.compare(today(getLocalTimeZone())) > 0)
        errors.publishedDate = "Future dates are not allowed";
    if (!form.type)
        errors.type = "Type is required";
    if (!form.contactName.trim())
        errors.contactName = "Contact name is required";
    if (!form.contactEmail.trim())
        errors.contactEmail = "Contact email is required";
    else if (!EMAIL_REGEX.test(form.contactEmail.trim()))
        errors.contactEmail = "Enter a valid email address";
    if (!form.category.trim())
        errors.category = "Category is required";
    if (!form.publication.trim())
        errors.publication = "Publication is required";
    if (!form.title.trim())
        errors.title = "Article / Placement title is required";
    if (!form.articleLink.trim())
        errors.articleLink = "Article link is required";
    if (!form.tag)
        errors.tag = "Tag is required";
    return errors;
};
const radioSx = { color: "#FF008E", "&.Mui-checked": { color: "#FF008E" } };
const AddPlacementDrawer = ({ open, onClose, publisherGroupId, publisherName, clientId, clientName, onSaved, duplicateFrom }) => {
    const token = useSelector((state) => state.auth?.token) || "";
    const designationWiseUsers = useSelector(selectDesignationWiseUsers);
    const allClients = useSelector(selectAllClientList);
    const [form, setForm] = useState(getInitialForm());
    // Errors only show after the first Save click, then follow the form while the user fixes the fields
    const [submitted, setSubmitted] = useState(false);
    const [publishers, setPublishers] = useState([]);
    const [publishersLoading, setPublishersLoading] = useState(false);
    // The client's Associate Affiliate Lead(s) (client_account_handlers) - shown under the Client dropdown, sent on save
    const [leads, setLeads] = useState([]);
    // The client's Account Manager user ids (client_account_handlers.am_id); null until loaded
    const [clientAmIds, setClientAmIds] = useState(null);
    const [saving, setSaving] = useState(false);
    const leadsReq = useRef(0);
    // The row-based publisher pre-selection runs once per open, so it never overrides the user's own choice
    const publisherPrefilled = useRef(false);
    // Latest-request counter so a slow response for a previous client never fills the list
    const publishersReq = useRef(0);
    // "Archived Client" switch above the Client dropdown: on = the dropdown lists the inactive (archived) affiliate clients.
    // They are not in the store's client list (active only), so they are fetched the first time the switch is turned on.
    const [showArchived, setShowArchived] = useState(false);
    const [archivedClients, setArchivedClients] = useState(null);
    const [archivedLoading, setArchivedLoading] = useState(false);
    // Presentation Mode: only the clients picked for it are listed (same as the toolbar Client filter of the page)
    const isPresentationMode = useSelector((state) => state.presentation.isPresentationMode);
    const presentationClients = useSelector((state) => state.presentation.presentationClients);
    const clientOptions = useMemo(() => {
        const presentationIds = new Set((presentationClients || []).map((c) => String(c?._id ?? c)));
        const toAffiliateOptions = (list) => list
            .filter((c) => Array.isArray(c.type) && c.type.includes("affiliate"))
            .filter((c) => !isPresentationMode || presentationIds.has(String(c._id)))
            .map((c) => ({ value: c._id, label: c.name }));
        return showArchived
            ? toAffiliateOptions(archivedClients || [])
            : toAffiliateOptions((allClients || []).filter((c) => c.status === "active"));
    }, [allClients, showArchived, archivedClients, isPresentationMode, presentationClients]);
    const loadArchivedClients = async () => {
        if (archivedClients)
            return;
        setArchivedLoading(true);
        try {
            const response = await ApiCall("GET", "/api/client-store/list-all?status=inactive", "", { Authorization: `Bearer ${token}` });
            const data = response?.data;
            if (data?.success)
                setArchivedClients(data.data?.clients || []);
            else
                toast.error(data?.message || "Failed to load archived clients");
        }
        catch {
            toast.error("Failed to load archived clients");
        }
        finally {
            setArchivedLoading(false);
        }
    };
    const handleArchivedToggle = (checked) => {
        setShowArchived(checked);
        // the chosen client (and its publisher) belongs to the other list
        setForm((prev) => ({ ...prev, clientId: "", publisher: "", accountManagerId: "" }));
        if (checked)
            loadArchivedClients();
    };
    // Form values of a duplicated placement (everything the Add Placement form has; a new placement is created on save)
    const formFromPlacement = (p) => {
        const published = p.publishedDate ? moment.utc(p.publishedDate) : null;
        const rate = p.commissionRate === "" || p.commissionRate === null || p.commissionRate === undefined || Number(p.commissionRate) === 0
            ? ""
            : String(p.commissionRate);
        return {
            ...getInitialForm(),
            clientId: p.clientId || "",
            accountManagerId: p.accountManagerId || "",
            publisher: p.publisherId || "",
            publishedDate: published ? new CalendarDate(published.year(), published.month() + 1, published.date()) : null,
            type: p.type || "",
            commissionRate: rate,
            contactName: p.contactName || "",
            contactEmail: p.contactEmail || "",
            category: p.category || "",
            publication: p.publication || "",
            title: p.title || "",
            articleLink: p.articleLink || "",
            payPerPlacement: p.payPerPlacement ? "yes" : "no",
            tag: (p.tags || [])[0] || "",
        };
    };
    // Fresh form every time the drawer opens (client pre-filled from the row it was opened from, or all the details of
    // the placement being duplicated)
    useEffect(() => {
        if (!open)
            return;
        publisherPrefilled.current = false;
        setSubmitted(false);
        setShowArchived(false);
        if (duplicateFrom) {
            setForm(formFromPlacement(duplicateFrom));
            // an archived client is only in the archived list
            const isActiveClient = (allClients || []).some((c) => c._id === duplicateFrom.clientId && c.status === "active");
            if (duplicateFrom.clientId && !isActiveClient) {
                setShowArchived(true);
                loadArchivedClients();
            }
            return;
        }
        setForm({
            ...getInitialForm(),
            clientId: clientId || (clientName ? clientOptions.find((o) => o.label === clientName)?.value || "" : ""),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, publisherGroupId, publisherName, clientId, clientName, duplicateFrom]);
    const accountManagerOptions = useMemo(() => collectUsersByDesignations(designationWiseUsers?.designationMap, AM_DESIGNATIONS).map((u) => ({
        value: u._id,
        label: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
    })), [designationWiseUsers]);
    // Publisher list depends on the Client: that client's affiliate_publishers, one option per distinct publisher_id
    useEffect(() => {
        publisherPrefilled.current = false;
        setPublishers([]);
        if (!open || !form.clientId) {
            setPublishersLoading(false);
            return;
        }
        const reqId = ++publishersReq.current;
        setPublishersLoading(true);
        ApiCall("GET", `/api/publisher-reporting/publishers?client_id=${encodeURIComponent(form.clientId)}`, undefined, {
            Authorization: token,
            "x-module-key": MODULE_KEY,
        })
            .then((response) => {
            if (reqId !== publishersReq.current)
                return;
            const data = response?.data;
            if (data?.success)
                setPublishers(data.data.publishers || []);
            else
                toast.error(data?.message || "Failed to load publishers");
        })
            .catch(() => { if (reqId === publishersReq.current)
            toast.error("Failed to load publishers"); })
            .finally(() => { if (reqId === publishersReq.current)
            setPublishersLoading(false); });
    }, [open, form.clientId, token]);
    // Associate Affiliate Lead(s) + Account Manager(s) of the selected client (none = nothing is shown / pre-selected)
    useEffect(() => {
        setLeads([]);
        setClientAmIds(null);
        if (!open || !form.clientId)
            return;
        const reqId = ++leadsReq.current;
        ApiCall("GET", `/api/publisher-reporting/associate-leads?client_id=${encodeURIComponent(form.clientId)}`, undefined, {
            Authorization: token,
            "x-module-key": MODULE_KEY,
        })
            .then((response) => {
            if (reqId !== leadsReq.current)
                return;
            const data = response?.data;
            if (data?.success) {
                setLeads(data.data.leads || []);
                setClientAmIds(data.data.accountManagerIds || []);
            }
        })
            .catch(() => undefined);
    }, [open, form.clientId, token]);
    // Default Account Manager of the selected client: its only AM, or - when it has several - the one with the
    // "Affiliate AM" designation. Only an AM that is in the dropdown can be pre-selected, and a choice the user
    // already made is never overridden.
    useEffect(() => {
        if (!open || !clientAmIds || !clientAmIds.length)
            return;
        const selectable = clientAmIds.filter((id) => accountManagerOptions.some((o) => o.value === id));
        let defaultAm = "";
        if (selectable.length === 1 && clientAmIds.length === 1) {
            defaultAm = selectable[0];
        }
        else if (selectable.length > 0) {
            const affiliateAmIds = new Set((designationWiseUsers?.designationMap?.[AFFILIATE_AM_DESIGNATION] || []).map((u) => u._id));
            defaultAm = selectable.find((id) => affiliateAmIds.has(id)) || "";
        }
        if (defaultAm)
            setForm((prev) => (prev.accountManagerId ? prev : { ...prev, accountManagerId: defaultAm }));
    }, [open, clientAmIds, accountManagerOptions, designationWiseUsers]);
    // Opened from a publisher row: once the client's publishers are loaded, pre-select that row's publisher (if the client has it)
    useEffect(() => {
        if (!open || publisherPrefilled.current || !publishers.length)
            return;
        if (!publisherGroupId && !publisherName)
            return;
        const match = publishers.find((p) => (publisherGroupId && p.group_publisher_id === publisherGroupId))
            || publishers.find((p) => publisherName && p.group_publisher_name === publisherName);
        publisherPrefilled.current = true;
        if (match)
            setForm((prev) => (prev.publisher ? prev : { ...prev, publisher: match.publisher_id }));
    }, [open, publishers, publisherGroupId, publisherName]);
    const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
    // Changing the client clears the publisher (it belonged to the previous client) and the Account Manager
    // (the new client's default AM is pre-selected again once its handlers are loaded)
    const handleClientChange = (clientId) => setForm((prev) => (prev.clientId === clientId ? prev : { ...prev, clientId, publisher: "", accountManagerId: "" }));
    const publisherOptions = useMemo(() => publishers.map((p) => ({ value: p.publisher_id, label: p.publisher_name })), [publishers]);
    const errors = useMemo(() => validate(form), [form]);
    // Message for a field, only once the user tried to save
    const err = (key) => (submitted ? errors[key] : undefined);
    const handleSave = async () => {
        setSubmitted(true);
        if (Object.keys(errors).length > 0) {
            toast.error("Please fill in all required fields correctly.");
            return;
        }
        const payload = {
            client_id: form.clientId,
            ...(form.accountManagerId && { am_id: form.accountManagerId }),
            // The client's Associate Affiliate Lead is stored in publish_articles.outreach_specialist (a single user:
            // the first one when the client has several)
            ...(leads.length > 0 && { outreach_specialist: leads[0]._id }),
            // affiliate_publishers.publisher_id of the selected Publisher / Partner
            publisher_id: form.publisher,
            published_date: form.publishedDate ? form.publishedDate.toString() : null,
            type: form.type,
            commission_rate: form.commissionRate.trim() === "" ? 0 : form.commissionRate.trim(),
            contact_name: form.contactName.trim(),
            contact_email: form.contactEmail.trim(),
            category: form.category.trim(),
            publication: form.publication.trim(),
            title: form.title.trim(),
            article_link: form.articleLink.trim(),
            pay_per_placement: form.payPerPlacement === "yes",
            tags: [form.tag],
            email_notification: form.emailNotification,
        };
        setSaving(true);
        try {
            const response = await ApiCall("POST", "/api/publisher-reporting/add-placement", payload, {
                Authorization: token,
                "x-module-key": MODULE_KEY,
            });
            const data = response?.data;
            if (data?.success) {
                toast.success("Placement saved successfully.");
                onSaved?.();
                onClose();
            }
            else {
                toast.error(data?.message || "Failed to save placement");
            }
        }
        catch {
            toast.error("Failed to save placement");
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: duplicateFrom ? "Duplicate Placement" : "Add Placement", width: "xl", customWidth: "1040px", headerActions: _jsx(AppButton, { variant: "contained", color: "primary", className: "app-common-button", size: "small", onClick: handleSave, loading: saving, startIcon: _jsx(Save, { size: 16 }), sx: { height: "36px", whiteSpace: "nowrap", fontSize: "13px", fontWeight: 500, gap: 1, px: 2, borderRadius: "6px" }, children: "Save" }), children: _jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 2, p: 2, bgcolor: "#fff", minHeight: "100%" }, children: [_jsxs(Box, { sx: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }, children: [_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.5 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between" }, children: [_jsxs("label", { className: "form-input-label", children: ["Client", _jsx("span", { className: "required-asterisk", children: "*" })] }), _jsx(Box, { sx: { display: "flex", alignItems: "center", background: "#F2F4F7", borderRadius: "8px", p: "3px", border: "1px solid #E9EAEB", height: "30px" }, children: [
                                                { label: "Active", archived: false, dot: "#12B76A" },
                                                { label: "Archive", archived: true, dot: "#667085" },
                                            ].map((tab) => {
                                                const selected = showArchived === tab.archived;
                                                return (_jsxs(Box, { onClick: () => { if (!selected)
                                                        handleArchivedToggle(tab.archived); }, sx: {
                                                        display: "flex", alignItems: "center", gap: 0.75, px: 1.25, height: "100%",
                                                        borderRadius: "6px", cursor: "pointer",
                                                        background: selected ? "#fff" : "transparent",
                                                        boxShadow: selected ? "0px 1px 2px rgba(16,24,40,0.05)" : "none",
                                                        transition: "all 0.2s ease",
                                                    }, children: [_jsx(Box, { sx: { width: 6, height: 6, borderRadius: "50%", background: selected ? tab.dot : "#98A2B3", transition: "all 0.2s ease" } }), _jsx(Typography, { sx: { fontWeight: 500, fontSize: "12px", color: selected ? "#101828" : "#667085", whiteSpace: "nowrap", transition: "all 0.2s ease" }, children: tab.label })] }, tab.label));
                                            }) })] }), _jsx(Dropdown, { value: form.clientId, options: clientOptions, onChange: (v) => handleClientChange(v), placeholder: archivedLoading ? "Loading archived clients..." : showArchived ? "Select Archived Client" : "Select Client", loading: archivedLoading, error: Boolean(err("clientId")), errorMessage: err("clientId"), searchable: true, usePortal: true })] }), _jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.5 }, children: [_jsx(Dropdown, { label: "Account Manager", value: form.accountManagerId, options: accountManagerOptions, onChange: (v) => setField("accountManagerId", v), placeholder: "Select Account Manager", searchable: true, usePortal: true }), leads.length > 0 && (_jsxs(Typography, { className: "all-Font-Sizes", sx: { fontSize: "12px !important", color: "#667085" }, children: ["Associated Outreach Specialist: ", leads.map((l) => l.name).join(", ")] }))] }), _jsx(Dropdown, { label: "Publisher / Partner", required: true, value: form.publisher, options: publisherOptions, onChange: (v) => setField("publisher", v), placeholder: !form.clientId ? "Select a client first" : publishersLoading ? "Loading publishers..." : "Select Publisher", disabled: !form.clientId, loading: publishersLoading, error: Boolean(err("publisher")), errorMessage: err("publisher"), searchable: true, usePortal: true }), _jsx(SingleDatePicker, { label: "Published Date", required: true, value: form.publishedDate, onChange: (date) => setField("publishedDate", date), placeholder: "MM/DD/YYYY", error: Boolean(err("publishedDate")), errorMessage: err("publishedDate"), allowToday: true, disableFuture: true }), _jsx(Dropdown, { label: "Type", required: true, value: form.type, options: TYPE_OPTIONS, onChange: (v) => setField("type", v), placeholder: "Select Type", error: Boolean(err("type")), errorMessage: err("type"), sortByLabel: false, usePortal: true }), _jsx(TextInput, { label: "Commission Rate", value: form.commissionRate, onChange: (e) => setField("commissionRate", e.target.value), placeholder: "e.g. 10" }), _jsx(TextInput, { label: "Contact Name", required: true, value: form.contactName, onChange: (e) => setField("contactName", e.target.value), placeholder: "Contact Name", error: Boolean(err("contactName")), errorMessage: err("contactName") }), _jsx(TextInput, { label: "Contact Email", required: true, type: "email", value: form.contactEmail, onChange: (e) => setField("contactEmail", e.target.value), placeholder: "Contact Email", error: Boolean(err("contactEmail")), errorMessage: err("contactEmail") }), _jsx(TextInput, { label: "Category", required: true, value: form.category, onChange: (e) => setField("category", e.target.value), placeholder: "Category", error: Boolean(err("category")), errorMessage: err("category") }), _jsx(TextInput, { label: "Publication", required: true, value: form.publication, onChange: (e) => setField("publication", e.target.value), placeholder: "Publication", error: Boolean(err("publication")), errorMessage: err("publication") })] }), _jsx(TextInput, { label: "Article / Placement Title", required: true, value: form.title, onChange: (e) => setField("title", e.target.value), placeholder: "Article / Placement Title", error: Boolean(err("title")), errorMessage: err("title") }), _jsx(TextInput, { label: "Article Link", required: true, value: form.articleLink, onChange: (e) => setField("articleLink", e.target.value), placeholder: "https://", error: Boolean(err("articleLink")), errorMessage: err("articleLink") }), _jsx(Dropdown, { label: "Tag", required: true, value: form.tag, options: TAG_OPTIONS, onChange: (v) => setField("tag", v), placeholder: "Select Tag", error: Boolean(err("tag")), errorMessage: err("tag"), sortByLabel: false, usePortal: true }), _jsxs(Box, { children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", fontWeight: 500, color: "#344054" }, children: "This client has a pay-per-placement agreement" }), _jsxs(RadioGroup, { row: true, value: form.payPerPlacement, onChange: (e) => setField("payPerPlacement", e.target.value), children: [_jsx(FormControlLabel, { value: "yes", className: "all-Font-Sizes", label: "Yes", control: _jsx(Radio, { sx: radioSx }) }), _jsx(FormControlLabel, { value: "no", className: "all-Font-Sizes", label: "No", control: _jsx(Radio, { sx: radioSx }) })] })] }), _jsxs(RadioGroup, { row: true, value: form.emailNotification, onChange: (e) => setField("emailNotification", e.target.value), children: [_jsx(FormControlLabel, { value: "email_affiliate_team", className: "all-Font-Sizes", label: "Notify Only Affiliate Team", control: _jsx(Radio, { sx: radioSx }) }), _jsx(FormControlLabel, { value: "email_team", className: "all-Font-Sizes", label: "Notify All", control: _jsx(Radio, { sx: radioSx }) })] })] }) }));
};
export default AddPlacementDrawer;
